/* agmf-feed-events.js — what the media feed actually showed, and what was done
   with it, reported to CAE.
   ===========================================================================
   THE GAP THIS CLOSES. Cards, reviews and clicks have reported to CAE for
   months, but the FLOWING MEDIA FEED reported nothing: no line in
   agilite-media-flow.js talks to the analytics client, and the "already shown"
   ledger (agmf-seen.js) lives in the shopper's own browser and never leaves
   it. So nobody could answer "what did this person actually see in the feed,
   what did they swipe past, what did they linger on" (Dorian 2026-09-03).

   ISOLATION PRINCIPLE. This file owns no state beyond its own observers. It
   reads the DOM (the stamps agilite-media-flow.js already puts on every card)
   and emits through the existing CAE client with window.CAEDev.emit(). It
   edits neither the flow nor the seen ledger nor cae-client.

   THE EVENTS, and what each means in words:
     feed_item_shown    it appeared on their screen at all
     feed_item_seen     ...and stayed there, half on screen, for about a second
     feed_item_opened   they acted on it: played the film, read the article
     feed_item_left     they moved on - carrying how long it was in view
   Each carries, in `detail`: mediaId (the Agilite Media record), ck (the
   feed's own content key), position and lap in the deal, the content type,
   and for `left` the dwell in milliseconds. product_handle, visitor_id,
   session_id, device, country and the rest are stamped by cae-client.

   `detail` is a JSON column in BigQuery, so these fields need no schema
   change and cannot be silently dropped the way an unknown top-level field
   would be.
   ========================================================================= */
(function () {
  'use strict';

  if (window.CAE_DISABLED === true) return;      // mirror cae-client's kill switch
  if (window.__agmfFeedEvents) return;
  window.__agmfFeedEvents = true;

  // ── ONE LAYER OWNS THE ANSWER (Dorian 2026-09-06) ────────────────────────
  // agmf-track.js arrived on 2026-09-03 asking the same question of the same
  // cards with a different threshold, different visibility maths and a
  // different key, and it answers more of it: the time on screen SPLIT by
  // whether the page was standing still, the card's place in the grid and
  // inside a swipeable rail, every click by name, and the two add-to-cart
  // buttons apart. Two layers on one page would disagree about the same card
  // and would share one 500-slot event queue that drops the OLDEST on
  // overflow - which is where session_start and add_to_cart live.
  //
  // So this file stands down WHERE THAT ONE RUNS, and nowhere else. Nothing
  // is deleted: it still loads on every page, and on any surface without the
  // media feed's own tracker it reports exactly as it always has. Rows
  // already in BigQuery keep their meaning; `feed_item_*` simply stops
  // growing on the product page.
  //
  // The SCRIPT TAG is the test, not a global: both files are deferred, this
  // one is in theme.liquid and the other in the feed's section, so this runs
  // first and a flag would always read absent. By execution time the document
  // is parsed and the tag is there.
  try {
    if (document.querySelector('script[src*="agmf-track.js"]')) return;
  } catch (e) { /* an odd document is not a reason to stop reporting */ }

  // Half the card, for about a second: the same threshold the seen ledger
  // uses for "dwelled", so a person reading this data and a person reading
  // the feed's own behaviour are talking about the same moment.
  var SEEN_RATIO = 0.5;
  var SEEN_MS = 1000;

  var _pending = [];
  var _ready = false;
  function emit(name, detail) {
    try {
      if (window.AGB_consoleCat && window.AGB_consoleCat('interaction')) {
        console.log('[agmf-events]', name, detail || {});
      }
    } catch (e) {}
    try {
      if (_ready && window.CAEDev && typeof window.CAEDev.emit === 'function') {
        window.CAEDev.emit(name, detail || {});
      } else {
        _pending.push([name, detail || {}]);
      }
    } catch (e) {}
  }
  (function waitForClient(tries) {
    if (window.CAEDev && typeof window.CAEDev.emit === 'function') {
      _ready = true;
      var queued = _pending.splice(0, _pending.length);
      for (var i = 0; i < queued.length; i++) emit(queued[i][0], queued[i][1]);
      return;
    }
    if ((tries || 0) > 60) return;               // ~30s, then give up quietly
    setTimeout(function () { waitForClient((tries || 0) + 1); }, 500);
  })(0);

  /** Everything we know about one card, from the stamps the feed already puts on it. */
  function about(el) {
    var d = el.dataset || {};
    var item = el.__agmfItem || {};
    var detail = {
      mediaId: d.agmfId || '',
      ck: d.agmfCk || '',
      contentType: d.agmfType || String(item.contentType || ''),
      platform: String(item.platform || ''),
      surface: el.closest('[data-agmf-card-surface]') ? 'product-card' : 'feed'
    };
    if (d.agmfIdx != null && d.agmfIdx !== '') detail.position = Number(d.agmfIdx);
    if (d.agmfLap != null && d.agmfLap !== '') detail.lap = Number(d.agmfLap);
    return detail;
  }

  /* ---- shown / seen / left ------------------------------------------- */

  var watched = new WeakMap();   // el -> { shown, seenTimer, seenSent, enteredAt }

  var observer = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var el = entry.target;
      var state = watched.get(el);
      if (!state) { state = {}; watched.set(el, state); }

      if (entry.isIntersecting) {
        if (!state.shown) {
          state.shown = true;
          emit('feed_item_shown', about(el));
        }
        state.enteredAt = state.enteredAt || Date.now();
        if (entry.intersectionRatio >= SEEN_RATIO && !state.seenSent && !state.seenTimer) {
          state.seenTimer = setTimeout(function (node, s) {
            return function () {
              s.seenTimer = null;
              s.seenSent = true;
              emit('feed_item_seen', about(node));
            };
          }(el, state), SEEN_MS);
        }
        continue;
      }

      // Out of view: a half-finished "seen" never happened, and a card that
      // WAS in view reports how long it held the screen.
      if (state.seenTimer) { clearTimeout(state.seenTimer); state.seenTimer = null; }
      if (state.enteredAt) {
        var detail = about(el);
        detail.dwellMs = Date.now() - state.enteredAt;
        detail.wasSeen = !!state.seenSent;
        state.enteredAt = 0;
        // A flick past is not a datum worth a row.
        if (detail.dwellMs >= 250) emit('feed_item_left', detail);
      }
    }
  }, { threshold: [0, SEEN_RATIO, 0.9] });

  /* ---- opened --------------------------------------------------------- */
  // The feed opens content in several ways (a play control, the lightbox, an
  // article reader). Rather than learn each one, this listens for the click
  // that lands INSIDE a card and asks the feed's own markup what happened.
  document.addEventListener('click', function (event) {
    var el = event.target && event.target.closest ? event.target.closest('[data-agmf-id]') : null;
    if (!el) return;
    // A CONTROL IS NOT AN OPENING. Everything inside [data-agmf-yt] counted
    // as "they played it", so pressing PAUSE, muting, seeking or closing was
    // being recorded as an open - on the one event that is supposed to mean
    // somebody chose this piece. (The badge is aria-hidden and takes no
    // pointer events, so it never reaches here at all.)
    if (event.target.closest('.agmf-ytc, [data-agmf-ytplay], [data-agmf-ytresume], ' +
        '[data-agmf-ytsound], [data-agmf-ytbar], [data-agmf-ytclose]')) return;
    var opener = event.target.closest(
      '[data-agmf-yt], [data-agmf-artopen], .agmf-play, .agmf-open, video, iframe, a[href]'
    );
    if (!opener) return;
    var detail = about(el);
    detail.how = opener.tagName === 'A' ? 'link'
      : opener.matches('[data-agmf-artopen]') ? 'read'
      : 'play';
    emit('feed_item_opened', detail);
  }, true);

  /* ---- keeping up with a feed that re-deals itself -------------------- */
  function watch(root) {
    (root || document).querySelectorAll('[data-agmf-id]').forEach(function (el) {
      if (el.__agmfWatched) return;
      el.__agmfWatched = true;
      observer.observe(el);
    });
  }
  watch(document);
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-agmf-id]')) {
          if (!node.__agmfWatched) { node.__agmfWatched = true; observer.observe(node); }
        } else if (node.querySelectorAll) {
          watch(node);
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Leaving the page ends every open view, so a dwell is never lost to a tab
  // being closed mid-scroll.
  window.addEventListener('pagehide', function () {
    document.querySelectorAll('[data-agmf-id]').forEach(function (el) {
      var state = watched.get(el);
      if (!state || !state.enteredAt) return;
      var detail = about(el);
      detail.dwellMs = Date.now() - state.enteredAt;
      detail.wasSeen = !!state.seenSent;
      state.enteredAt = 0;
      emit('feed_item_left', detail);
    });
    try { if (window.CAEDev && window.CAEDev.flushNow) window.CAEDev.flushNow(); } catch (e) {}
  });
})();
